import {
  GridRowId,
  GridRowSelectionModel,
  GridSortDirection,
} from "@mui/x-data-grid-pro";
import { Dayjs } from "dayjs";
import { DealerAccount } from "../../../../api/dealer/types";
import {
  InvoiceHeaderDetailsV2,
  InvoicesResponse,
} from "../../../../api/invoices/interfaces";

export enum ListInvoiceActionKind {
  SET_INVOICES = "SET_INVOICES",
  SET_IS_LOADING = "SET_IS_LOADING",
  SET_APPLIED_FILTER = "SET_APPLIED_FILTER",
  PREVIOUS_PAGE = "PREVIOUS_PAGE",
  NEXT_PAGE = "NEXT_PAGE",
  FIRST_PAGE = "FIRST_PAGE",
  LAST_PAGE = "LAST_PAGE",
  SET_PAGE_ROWS_SIZE = "SET_PAGE_ROWS_SIZE",
  SET_FILTER = "SET_FILTER",
  CLEAR_FILTER = "CLEAR_FILTER",
  SET_NUMBER_OF_FILTER_CHANGES = "SET_NUMBER_OF_FILTER_CHANGES",
  SET_DATE_RANGE_FILTER_ACTION = "SET_DATE_RANGE_FILTER_ACTION",
  SET_SORT_PARAMS_ACTION = "SET_SORT_PARAMS_ACTION",
  SET_ROW_SELECTION_MODEL = "SET_ROW_SELECTION_MODEL",
  SET_SELECT_ALL = "SET_SELECT_ALL",
  SET_SELECT_ALL_NEW = "SET_SELECT_ALL_NEW",
  FETCH_INVOICE_DETAILS_COMPLETE = "FETCH_INVOICE_DETAILS_COMPLETE",
  SET_IS_ACTIONS_LOADING = "SET_IS_ACTIONS_LOADING",
  SET_ERROR = "SET_ERROR",
  DOWNLOADING_NFE_NUMBER = "DOWNLOADING_NFE_NUMBER",
  UPDATE_SELECTED_ACCOUNTS = "UPDATE_SELECTED_ACCOUNTS",
  UPDATE_DEALER_ACCOUNT = "UPDATE_DEALER_ACCOUNT",
  HEADER_SELECT_CHANGE = "HEADER_SELECT_CHANGE",
  ROW_SELECT_CHANGE = "ROW_SELECT_CHANGE",
  SET_D2D_ORDER_INVOICE_ENABLED = "SET_D2D_ORDER_INVOICE_ENABLED",
  SET_LANGUAGE = "SET_LANGUAGE",
  CLEAN_FILTERS = "CLEAN_FILTERS",
}

type SetInvoicesAction = {
  type: ListInvoiceActionKind.SET_INVOICES;
  payload: InvoicesResponse;
};

type SetIsLoadingAction = {
  type: ListInvoiceActionKind.SET_IS_LOADING;
  payload: boolean;
};

type SetAppliedFilterAction = {
  type: ListInvoiceActionKind.SET_APPLIED_FILTER;
};

type PreviousPageAction = {
  type: ListInvoiceActionKind.PREVIOUS_PAGE;
};

type NextPageAction = {
  type: ListInvoiceActionKind.NEXT_PAGE;
};

type FirstPageAction = {
  type: ListInvoiceActionKind.FIRST_PAGE;
};

type LastPageAction = {
  type: ListInvoiceActionKind.LAST_PAGE;
};

type SetPageRowsSizeAction = {
  type: ListInvoiceActionKind.SET_PAGE_ROWS_SIZE;
  payload: number;
};

type SetFilterAction = {
  type: ListInvoiceActionKind.SET_FILTER;
  payload: { value: string; type: string };
};

type SetClearFilterAction = {
  type: ListInvoiceActionKind.CLEAR_FILTER;
  payload: string;
};

type SetNumberOfFilterChangesAction = {
  type: ListInvoiceActionKind.SET_NUMBER_OF_FILTER_CHANGES;
  payload: number;
};

type SetDateRangeFilterAction = {
  type: ListInvoiceActionKind.SET_DATE_RANGE_FILTER_ACTION;
  payload: {
    startDate: Dayjs;
    endDate: Dayjs;
  };
};

type SetSortParamsAction = {
  type: ListInvoiceActionKind.SET_SORT_PARAMS_ACTION;
  payload: { field?: string; sort?: GridSortDirection };
};

type SetRowSelectionModel = {
  type: ListInvoiceActionKind.SET_ROW_SELECTION_MODEL;
  payload: GridRowSelectionModel;
};

type SetSelectAll = {
  type: ListInvoiceActionKind.SET_SELECT_ALL;
  payload: boolean;
};

type SetSelectAllNew = {
  type: ListInvoiceActionKind.SET_SELECT_ALL_NEW;
  payload: boolean;
};

type FetchInvoiceDetailsCompleteAction = {
  type: ListInvoiceActionKind.FETCH_INVOICE_DETAILS_COMPLETE;
  payload: InvoiceHeaderDetailsV2;
};

type SetIsActionsLoadingAction = {
  type: ListInvoiceActionKind.SET_IS_ACTIONS_LOADING;
  payload: boolean;
};

type SetErrorAction = {
  type: ListInvoiceActionKind.SET_ERROR;
  payload: any;
};

type DownloadingNfeNumberAction = {
  type: ListInvoiceActionKind.DOWNLOADING_NFE_NUMBER;
  payload: string;
};

type UpdateSelectedAccountsAction = {
  type: ListInvoiceActionKind.UPDATE_SELECTED_ACCOUNTS;
  payload: DealerAccount[];
};

type UpdateDealerAccountAction = {
  type: ListInvoiceActionKind.UPDATE_DEALER_ACCOUNT;
  payload: DealerAccount;
};

type HeaderSelectChangeAction = {
  type: ListInvoiceActionKind.HEADER_SELECT_CHANGE;
  payload: boolean;
};

type RowSelectChangeAction = {
  type: ListInvoiceActionKind.ROW_SELECT_CHANGE;
  payload: {
    value: boolean;
    rowId: GridRowId;
  };
};

type SetD2DOrderInvoiceEnabledAction = {
  type: ListInvoiceActionKind.SET_D2D_ORDER_INVOICE_ENABLED;
  payload: boolean;
};

type SetLanguageAction = {
  type: ListInvoiceActionKind.SET_LANGUAGE;
  payload: string;
};

type CleanFiltersAction = {
  type: ListInvoiceActionKind.CLEAN_FILTERS;
};

export type ListInvoiceActions =
  | SetInvoicesAction
  | SetIsLoadingAction
  | SetAppliedFilterAction
  | PreviousPageAction
  | NextPageAction
  | FirstPageAction
  | LastPageAction
  | SetFilterAction
  | SetPageRowsSizeAction
  | SetClearFilterAction
  | SetNumberOfFilterChangesAction
  | SetDateRangeFilterAction
  | SetSortParamsAction
  | SetRowSelectionModel
  | SetSelectAll
  | SetSelectAllNew
  | FetchInvoiceDetailsCompleteAction
  | SetIsActionsLoadingAction
  | SetErrorAction
  | DownloadingNfeNumberAction
  | UpdateSelectedAccountsAction
  | HeaderSelectChangeAction
  | RowSelectChangeAction
  | SetD2DOrderInvoiceEnabledAction
  | SetLanguageAction
  | CleanFiltersAction
  | UpdateDealerAccountAction;

export function setInvoicesAction(
  invoices: InvoicesResponse
): SetInvoicesAction {
  return {
    type: ListInvoiceActionKind.SET_INVOICES,
    payload: invoices,
  };
}

export function setIsLoadingAction(isLoading: boolean): SetIsLoadingAction {
  return {
    type: ListInvoiceActionKind.SET_IS_LOADING,
    payload: isLoading,
  };
}

export function previousPageAction(): PreviousPageAction {
  return {
    type: ListInvoiceActionKind.PREVIOUS_PAGE,
  };
}

export function nextPageAction(): NextPageAction {
  return {
    type: ListInvoiceActionKind.NEXT_PAGE,
  };
}

export function firstPageAction(): FirstPageAction {
  return {
    type: ListInvoiceActionKind.FIRST_PAGE,
  };
}

export function lastPageAction(): LastPageAction {
  return {
    type: ListInvoiceActionKind.LAST_PAGE,
  };
}

export function setFilterAction(value: string, type: string): SetFilterAction {
  return {
    type: ListInvoiceActionKind.SET_FILTER,
    payload: { value, type },
  };
}

export function setPageRowsSizeAction(
  pageRowSize: number
): SetPageRowsSizeAction {
  return {
    type: ListInvoiceActionKind.SET_PAGE_ROWS_SIZE,
    payload: pageRowSize,
  };
}

export function setClearFilterAction(type: string): SetClearFilterAction {
  return {
    type: ListInvoiceActionKind.CLEAR_FILTER,
    payload: type,
  };
}

export function setDateRangeFilterAction(
  startDate: Dayjs,
  endDate: Dayjs
): SetDateRangeFilterAction {
  return {
    type: ListInvoiceActionKind.SET_DATE_RANGE_FILTER_ACTION,
    payload: {
      startDate,
      endDate,
    },
  };
}

export function setSortParamsAction(
  field?: string,
  sort?: GridSortDirection
): SetSortParamsAction {
  return {
    type: ListInvoiceActionKind.SET_SORT_PARAMS_ACTION,
    payload: { field, sort },
  };
}

export function setRowSelectionModel(
  newRowSelectionModel: GridRowSelectionModel
): SetRowSelectionModel {
  return {
    type: ListInvoiceActionKind.SET_ROW_SELECTION_MODEL,
    payload: newRowSelectionModel,
  };
}

export function setSelectAll(selectAll: boolean): SetSelectAll {
  return {
    type: ListInvoiceActionKind.SET_SELECT_ALL,
    payload: selectAll,
  };
}

export function setSelectAllNew(selectAllNew: boolean): SetSelectAllNew {
  return {
    type: ListInvoiceActionKind.SET_SELECT_ALL_NEW,
    payload: selectAllNew,
  };
}

export function fetchInvoiceDetailsCompleteAction(
  invoices: InvoiceHeaderDetailsV2
): FetchInvoiceDetailsCompleteAction {
  return {
    type: ListInvoiceActionKind.FETCH_INVOICE_DETAILS_COMPLETE,
    payload: invoices,
  };
}

export function setIsActionsLoadingAction(
  isLoading: boolean
): SetIsActionsLoadingAction {
  return {
    type: ListInvoiceActionKind.SET_IS_ACTIONS_LOADING,
    payload: isLoading,
  };
}

export function setErrorAction(error: any): SetErrorAction {
  return {
    type: ListInvoiceActionKind.SET_ERROR,
    payload: error,
  };
}

export function downloadingNfeNumberAction(
  nfeNumber: string
): DownloadingNfeNumberAction {
  return {
    type: ListInvoiceActionKind.DOWNLOADING_NFE_NUMBER,
    payload: nfeNumber,
  };
}

export function updateSelectedAccountsAction(
  accounts: DealerAccount[]
): UpdateSelectedAccountsAction {
  return {
    type: ListInvoiceActionKind.UPDATE_SELECTED_ACCOUNTS,
    payload: accounts,
  };
}

export function updateDealerAccountAction(
  dealerAccount: DealerAccount
): UpdateDealerAccountAction {
  return {
    type: ListInvoiceActionKind.UPDATE_DEALER_ACCOUNT,
    payload: dealerAccount,
  };
}

export function setD2DOrderInvoiceEnabledAction(
  enabled: boolean
): SetD2DOrderInvoiceEnabledAction {
  return {
    type: ListInvoiceActionKind.SET_D2D_ORDER_INVOICE_ENABLED,
    payload: enabled,
  };
}

export function headerSelectChangeAction(
  isSelected: boolean
): HeaderSelectChangeAction {
  return {
    type: ListInvoiceActionKind.HEADER_SELECT_CHANGE,
    payload: isSelected,
  };
}

export function rowSelectChangeAction(
  value: boolean,
  rowId: GridRowId
): RowSelectChangeAction {
  return {
    type: ListInvoiceActionKind.ROW_SELECT_CHANGE,
    payload: { value, rowId },
  };
}

export function setLanguageAction(language: string): SetLanguageAction {
  return {
    type: ListInvoiceActionKind.SET_LANGUAGE,
    payload: language,
  };
}

export function cleanFiltersAction(): CleanFiltersAction {
  return {
    type: ListInvoiceActionKind.CLEAN_FILTERS,
  };
}
